import { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { loadable } from 'jotai/utils';
import { ITEM_CATEGORY, DETECTION_ITEM_DEF_MAP } from '@/constants';
import {
  ItemDefinitionsAtom,
  itemsAtom,
  routeRoomAtom,
  selectedSegmentIdAtom,
} from '@/store';
import type { ItemDefinition } from '@/types';
import { upsertItemWithItemDefinition } from '@/utils';
import {
  DETECTION_SURVEYED_CHANNEL,
  type SurveyedItemCounts,
} from './useDetectionAutoSurvey';

const loadableItemDefs = loadable(ItemDefinitionsAtom);

// Runs in the main window. Listens for surveyed item counts posted by the
// video popup and applies them to the currently open room.
export function useDetectionItemsListener() {
  const room = useAtomValue(routeRoomAtom);
  const segmentId = useAtomValue(selectedSegmentIdAtom);
  const itemDefsLoadable = useAtomValue(loadableItemDefs);
  const setItems = useSetAtom(itemsAtom);

  useEffect(() => {
    console.log(
      '[ItemsListener] mounted — room:',
      room?.id,
      'segmentId:',
      segmentId,
      'itemDefs:',
      itemDefsLoadable.state
    );
    const handler = (event: MessageEvent) => {
      if (event.data?.type !== DETECTION_SURVEYED_CHANNEL) return;
      console.log(
        '[ItemsListener] message received',
        event.data,
        'origin:',
        event.origin
      );
      if (event.origin !== window.location.origin) {
        console.warn(
          '[ItemsListener] origin mismatch — expected',
          window.location.origin,
          'got',
          event.origin
        );
        return;
      }
      if (!room) {
        console.warn('[ItemsListener] no room open, ignoring');
        return;
      }

      const itemDefinitions: ItemDefinition[] =
        itemDefsLoadable.state === 'hasData' ? itemDefsLoadable.data : [];
      if (itemDefinitions.length === 0) {
        console.warn(
          '[ItemsListener] item definitions not loaded yet (state:',
          itemDefsLoadable.state,
          ')'
        );
        return;
      }

      const counts = event.data.items as SurveyedItemCounts;
      console.log(
        '[ItemsListener] applying counts',
        counts,
        'to room',
        room.id
      );
      const defsByName = new Map(
        itemDefinitions.map((d) => [d.name.toLowerCase().trim(), d])
      );

      setItems((prev) => {
        let items = prev;
        for (const [description, count] of Object.entries(counts)) {
          const mappedName = DETECTION_ITEM_DEF_MAP[description] ?? description;
          const def = defsByName.get(mappedName.toLowerCase().trim());
          if (!def) {
            console.warn(
              '[ItemsListener] no ItemDefinition match for description:',
              description,
              '→',
              mappedName
            );
            continue;
          }
          for (let i = 0; i < count; i++) {
            items = upsertItemWithItemDefinition(
              items,
              {
                name: def.name,
                weight: def.weight,
                cube: def.cube,
                isoCode: def.isoCode,
                roomId: room.id,
                segmentId: segmentId ?? '',
                category: ITEM_CATEGORY.HOUSEHOLD_GOODS,
                going: 1,
                notGoing: 0,
                definitionId: def.id,
              },
              itemDefinitions
            );
          }
        }
        console.log('[ItemsListener] items updated');
        return items;
      });
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [room, segmentId, itemDefsLoadable, setItems]);
}
